import React, { useState, useEffect, useCallback } from 'react';
import { CVBuilderProps, CVProfile } from './types';
import { cvService, CVServiceError } from '../../services/cvService';

// Import CV section components
import CVIdentityForm from './CVIdentityForm';
import CVEducationManager from './CVEducationManager';
import CVExperienceManager from './CVExperienceManager';
import CVSkillsSelector from './CVSkillsSelector';
import CVAdditionalSections from './CVAdditionalSections';
import CVExportSection from './CVExportSection';
import CVNavigation from './CVNavigation';

// Import error handling components
import ErrorBoundary from './components/ErrorBoundary';
import ErrorMessage from './components/ErrorMessage';

// Import styles
import './CVNavigation.scss';
import { Play, Sparkles } from "lucide-react";
import Button from "../UI/Button";
import { Card } from "../UI/Card";

/**
 * CV Builder - Simple full-scroll layout
 */
const CVBuilder: React.FC<CVBuilderProps> = ({ userId, onSave, onExport }) => {
  // Core state management
  const [isLoading, setIsLoading] = useState(false);
  const [cvData, setCvData] = useState<CVProfile | null>(null);
  const [skillsOptions, setSkillsOptions] = useState<{
    technicalInterests: string[];
    softSkills: string[];
  }>({
    technicalInterests: [],
    softSkills: []
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadError, setLoadError] = useState<CVServiceError | null>(null);

  // Format date from API (remove timezone and return YYYY-MM-DD format)
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0];
    } catch {
      return dateString;
    }
  };

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      setIsInitialLoading(true);
      setLoadError(null);

      try {
        const [cvData, technicalOptions, softOptions] = await Promise.all([
          cvService.getCVByUser(),
          cvService.getTechnicalInterestOptions(),
          cvService.getSoftSkillOptions()
        ]);

        // Format birth_date if it exists
        if (cvData && cvData.birth_date) {
          cvData.birth_date = formatDate(cvData.birth_date);
        }

        setCvData(cvData);
        setSkillsOptions({
          technicalInterests: technicalOptions,
          softSkills: softOptions
        });

      } catch (error: any) {
        if (error instanceof CVServiceError) {
          setLoadError(error);
        } else {
          setLoadError(new CVServiceError(
            error.message || 'Gagal memuat data CV. Silakan coba lagi.',
            undefined,
            error
          ));
        }
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadInitialData();
  }, []);

  // Update CV data in state
  const updateCVData = useCallback((newCvData: CVProfile) => {
    // Format birth_date if it exists
    if (newCvData.birth_date) {
      newCvData.birth_date = formatDate(newCvData.birth_date);
    }
    
    setCvData(newCvData);
    if (onSave) {
      onSave(newCvData);
    }
  }, [onSave]);

  // Retry failed operations
  const retryOperation = useCallback(async () => {
    if (loadError) {
      window.location.reload();
    }
  }, [loadError]);

  const SectionHeader = ({text}: {text: string}) => {
    return (
      <div className="px-4 border-b bg-gray-50 rounded-t-lg bg-primary text-white py-3">
        <h3 className="mb-0 font-extrabold">{text}</h3>
      </div>
    )
  }

  // Loading state
  if (isInitialLoading) {
    return (
      <div className="cv-builder-loading mt-5">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center">
            <div className="md:w-6/12 text-center">
              <h4>Memuat CV...</h4>
              <p className="text-gray-500">Mohon tunggu sebentar.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    return (
      <div className="cv-builder-error">
        <div className="container mx-auto">
          <div className="flex flex-wrap justify-center">
            <div className="md:w-8/12">
              <ErrorMessage
                title="Error Memuat CV Builder"
                message={loadError.message}
                error={loadError.originalError}
                onRetry={loadError.isRetryable ? retryOperation : undefined}
                retryLabel="Coba Lagi"
                showDetails={true}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        console.error("CV Builder Error:", error, errorInfo);
      }}
    >
      <div className="cv-builder">
        {/* Navigation - Inside cv-builder for proper sticky context */}
        <CVNavigation />

        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex flex-wrap mb-4">
            <div className="w-full">
              <div className="text-center">
                <h1 className="mb-2 font-bold text-2xl text-foreground">
                  CV Builder Berstandar ATS
                </h1>
              </div>
            </div>
          </div>
          <Card className="p-4 mb-4">
            <p className="text-gray-700 text-justify mb-3">
              Bangun CV profesional yang mudah dibaca sistem ATS dan recruiter.
              Dilengkapi panduan cerdas agar CV kamu rapi, relevan, dan siap
              kirim kerja.
            </p>
          </Card>

          {/* Personal Information */}
          <div id="personal-info" className="flex flex-wrap mb-5">
            <div className="w-full">
              <div className="bg-white rounded-lg shadow border">
                <SectionHeader text={"Informasi Pribadi"} />
                <div className="p-4">
                  <CVIdentityForm
                    initialData={
                      cvData
                        ? {
                            full_name: cvData.full_name,
                            birth_place: cvData.birth_place,
                            birth_date: cvData.birth_date,
                            address: cvData.address,
                            phone_number: cvData.phone_number,
                            email: cvData.email,
                            linkedin_url: cvData.linkedin_url,
                            portfolio_url: cvData.portfolio_url,
                            dream_job: cvData.dream_job,
                            main_interest: cvData.main_interest,
                            interest_reason: cvData.interest_reason,
                            profile_summary: cvData.profile_summary,
                            photo: undefined, // File will be handled by the form
                          }
                        : undefined
                    }
                    currentPhotoUrl={cvData?.photo_url}
                    onSave={async (data) => {
                      setIsLoading(true);
                      try {
                        let updatedCV: CVProfile;
                        if (cvData?.id) {
                          updatedCV = await cvService.updateCV(cvData.id, data);
                        } else {
                          updatedCV = await cvService.createCV(data);
                        }
                        updateCVData(updatedCV);
                      } finally {
                        setIsLoading(false);
                      }
                    }}
                    isLoading={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Education */}
          {cvData && (
            <div id="education" className="flex flex-wrap mb-5">
              <div className="w-full">
                <div className="bg-white rounded-lg shadow border">
                  <SectionHeader text={"Pendidikan"} />
                  <div className="p-4">
                    <CVEducationManager
                      educations={cvData.educations}
                      onAdd={async (education) => {
                        const updatedCV = await cvService.addEducation(
                          cvData.id,
                          education
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdate={async (educationId, education) => {
                        const updatedCV = await cvService.updateEducation(
                          cvData.id,
                          educationId,
                          education
                        );
                        updateCVData(updatedCV);
                      }}
                      onDelete={async (educationId) => {
                        const updatedCV = await cvService.deleteEducation(
                          cvData.id,
                          educationId
                        );
                        updateCVData(updatedCV);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Experience & Projects */}
          {cvData && (
            <div id="experience" className="flex flex-wrap mb-5">
              <div className="w-full">
                <div className="bg-white rounded-lg shadow border">
                  <SectionHeader text={"Pengalaman & Proyek"} />
                  <div className="p-4">
                    <CVExperienceManager
                      experiences={cvData.experiences}
                      projects={cvData.projects}
                      onAddExperience={async (experience) => {
                        const updatedCV = await cvService.addExperience(
                          cvData.id,
                          experience
                        );
                        updateCVData(updatedCV);
                      }}
                      onAddProject={async (project) => {
                        const updatedCV = await cvService.addProject(
                          cvData.id,
                          project
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdateExperience={async (experienceId, experience) => {
                        const updatedCV = await cvService.updateExperience(
                          cvData.id,
                          experienceId,
                          experience
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdateProject={async (projectId, project) => {
                        const updatedCV = await cvService.updateProject(
                          cvData.id,
                          projectId,
                          project
                        );
                        updateCVData(updatedCV);
                      }}
                      onDeleteExperience={async (experienceId) => {
                        const updatedCV = await cvService.deleteExperience(
                          cvData.id,
                          experienceId
                        );
                        updateCVData(updatedCV);
                      }}
                      onDeleteProject={async (projectId) => {
                        const updatedCV = await cvService.deleteProject(
                          cvData.id,
                          projectId
                        );
                        updateCVData(updatedCV);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Skills */}
          {cvData && (
            <div id="skills" className="flex flex-wrap mb-5">
              <div className="w-full">
                <div className="bg-white rounded-lg shadow border">
                  <SectionHeader text={"Keahlian & Minat"} />
                  <div className="p-4">
                    <CVSkillsSelector
                      technicalInterests={cvData.technical_interests}
                      softSkills={cvData.soft_skills}
                      hardSkills={cvData.hard_skills}
                      availableOptions={skillsOptions}
                      onUpdateTechnicalInterests={async (
                        interests,
                        customInterests
                      ) => {
                        const updatedCV =
                          await cvService.updateTechnicalInterests(cvData.id, {
                            interests,
                            customInterests,
                          });
                        updateCVData(updatedCV);
                      }}
                      onUpdateSoftSkills={async (skills) => {
                        const updatedCV = await cvService.updateSoftSkills(
                          cvData.id,
                          { skills }
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdateHardSkills={async (skills) => {
                        const updatedCV = await cvService.updateHardSkills(
                          cvData.id,
                          { skills }
                        );
                        updateCVData(updatedCV);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Additional Information */}
          {cvData && (
            <div id="additional" className="flex flex-wrap mb-5">
              <div className="w-full">
                <div className="bg-white rounded-lg shadow border">
                  <SectionHeader text={"Informasi Tambahan"} />
                  <div className="p-4">
                    <CVAdditionalSections
                      languages={cvData.languages}
                      achievements={cvData.achievements}
                      portfolios={cvData.portfolios}
                      onAddLanguage={async (language) => {
                        const updatedCV = await cvService.addLanguage(
                          cvData.id,
                          language
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdateLanguage={async (languageId, language) => {
                        const updatedCV = await cvService.updateLanguage(
                          cvData.id,
                          languageId,
                          language
                        );
                        updateCVData(updatedCV);
                      }}
                      onDeleteLanguage={async (languageId) => {
                        const updatedCV = await cvService.deleteLanguage(
                          cvData.id,
                          languageId
                        );
                        updateCVData(updatedCV);
                      }}
                      onAddAchievement={async (achievement) => {
                        const updatedCV = await cvService.addAchievement(
                          cvData.id,
                          achievement
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdateAchievement={async (
                        achievementId,
                        achievement
                      ) => {
                        const updatedCV = await cvService.updateAchievement(
                          cvData.id,
                          achievementId,
                          achievement
                        );
                        updateCVData(updatedCV);
                      }}
                      onDeleteAchievement={async (achievementId) => {
                        const updatedCV = await cvService.deleteAchievement(
                          cvData.id,
                          achievementId
                        );
                        updateCVData(updatedCV);
                      }}
                      onAddPortfolio={async (portfolio) => {
                        const updatedCV = await cvService.addPortfolio(
                          cvData.id,
                          portfolio
                        );
                        updateCVData(updatedCV);
                      }}
                      onUpdatePortfolio={async (portfolioId, portfolio) => {
                        const updatedCV = await cvService.updatePortfolio(
                          cvData.id,
                          portfolioId,
                          portfolio
                        );
                        updateCVData(updatedCV);
                      }}
                      onDeletePortfolio={async (portfolioId) => {
                        const updatedCV = await cvService.deletePortfolio(
                          cvData.id,
                          portfolioId
                        );
                        updateCVData(updatedCV);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Export CV */}
          {cvData && (
            <div id="export" className="flex flex-wrap mb-5">
              <div className="w-full">
                <div className="bg-white rounded-lg shadow border">
                  <div className="p-4">
                    <CVExportSection cvData={cvData} onExport={onExport} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default CVBuilder;